import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layout, Select, Input, Tabs, Dropdown, Typography, Modal, Button, Checkbox, Skeleton } from "antd";
import {
  CloseOutlined,
  DownOutlined,
  HeartOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import LogoPrimary from "../../assets/images/primary_logo.svg";
import { useAppDispatch, useAppSelector } from "../../hooks/useTypedSelector";
import { degreeSearchChange, selectedDegreeChanage, setSearchedList, setIsCourse, setCourseCipCode, clearAllFilters, setIsRole, setCourseSocCode, setSearchType } from "../../redux/searchSlice";
import "./NewAppHeader.css";
import { blackFilledBtn } from "../common/CommonComponents";
import API from "../../services/axiosInterceptor";
import ToastMsg from "../common/ToastMsg";
import { setIsAllReInfoFilled, updateUserField } from "../../redux/userSlice";
import searchCloseIcon from '../../assets/icons/searchCloseIcon.svg'
import crownIcon from '../../assets/icons/premiumCrown.svg'
import { DEGREE_OPTIONS } from "../../utils/Contants";
import { useAuth } from '../../hooks/useAuth';
import RotatingWords from '../common/RotatingWords';
import UserMenu from "../collegeDetails/UserMenuCard";
import { setIsUpgraded } from "../../redux/upgradeSlice";
import { setMobileUnverifiedUser } from "../../redux/mobileVerificationSlice";
import { setCurrentPlan, setIsFreeUser } from "../../redux/userSlice";
import lockIcon from '../../assets/icons/lock.svg';
import aiIcon from "../../assets/icons/ai.svg";
import searchIcon from "../../assets/icons/searchHeader.svg";

const { Header } = Layout;
const { Text } = Typography


const forcedVisibleTabs = [
  { key: '/recommend-college-dream', label: 'Match', icon: <img src={aiIcon} style={{ width: "20px", height: "20px" }} alt="match" /> },
  { key: '/search', label: 'Search', icon: <img src={searchIcon} style={{ width: "20px", height: "20px" }} alt="search" /> },
  { key: '/mylist-college', label: 'My List', icon: <HeartOutlined /> },
];

const forcedOverflowTabs = [
  { key: '/', label: 'Home' },
  { key: '/college', label: 'College' },
  { key: '/course', label: 'Majors' },
  { key: '/role', label: 'Role' },
  { key: '/compare-college', label: 'Compare' },
];

const flexTabs: { key: string; label: string; icon?: React.ReactNode }[] = [];


type NewAppHeaderProps = {
  onOpenMobileVerificationModal?: () => void;
}

function NewAppHeader({ onOpenMobileVerificationModal }: NewAppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { logout, user, isAuthenticated } = useAuth();


  const { selectedDegree, searchText, searchedList, searchType } = useAppSelector((state) => state.searchData);

  useEffect(() => {
    if (location.pathname === '/search') {
      const storedSearch = localStorage.getItem('selectedSearchText');
      if (storedSearch) {
        dispatch(degreeSearchChange(storedSearch));
      } else {
        dispatch(degreeSearchChange(''));
      }
    }
  }, [dispatch, location.pathname]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const avatar = useAppSelector((state) => state.auth.avatar);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser);
  const { isUpgraded } = useAppSelector((state) => state.upgrade)
  const mobileUnverifiedUser = useAppSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const { currentPlan, freeUser } = useAppSelector((state: any) => state.userData);
  const navTabsRef = useRef<HTMLDivElement>(null);
  const [visibleTabs, setVisibleTabs] = useState<{ key: string; label: string; icon?: React.ReactNode }[]>([...forcedVisibleTabs, ...flexTabs]);
  const [overflowTabs, setOverflowTabs] = useState<any[]>([...forcedOverflowTabs]);
  const [tabsKey, setTabsKey] = useState(0);

  useEffect(() => {
    function updateTabs() {
      if (!navTabsRef.current) return;

      const containerWidth = navTabsRef.current.offsetWidth;
      const tabWidths = flexTabs.map(() => 90);
      const visibleWidth = forcedVisibleTabs.length * 90;
      const moreWidth = 48;

      let used = visibleWidth;
      let lastVisible = flexTabs.length;

      for (let i = 0; i < flexTabs.length; i++) {
        if (used + tabWidths[i] + moreWidth > containerWidth) {
          lastVisible = i;
          break;
        }
        used += tabWidths[i];
      }

      const visibleFlex = flexTabs.slice(0, lastVisible);
      const overflowFlex = [
        ...forcedOverflowTabs,
        ...flexTabs.slice(lastVisible),
      ];

      setVisibleTabs([...forcedVisibleTabs, ...visibleFlex]);
      setOverflowTabs(overflowFlex);
    }

    updateTabs();
    window.addEventListener('resize', updateTabs);
    const rafId = requestAnimationFrame(() => updateTabs());
    const timeoutId = window.setTimeout(() => updateTabs(), 50);
    return () => {
      window.removeEventListener('resize', updateTabs);
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [windowWidth, location.pathname]);

  // Force Tabs re-render on location change
  useEffect(() => {
    setTabsKey(prev => prev + 1);
  }, [location.pathname]);




  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    }
    if (isDropdownVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownVisible]);

  const [activeIndex, setActiveIndex] = useState<number>(-1);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isDropdownVisible || !searchedList.length) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % searchedList.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + searchedList.length) % searchedList.length);
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < searchedList.length) {
          handleCollegeNav(searchedList[activeIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownVisible, searchedList, activeIndex]);



  const getUserPreference = useCallback(async () => {
    if (!isAuthenticated || sessionStorage.getItem('is_logging_out') === 'true') {
      console.log('User not authenticated or logging out, skipping preference fetch');
      return;
    }

    const doFetch = async () => {
      try {
        const response = await API.get('user-preference')
        if (response.data.data) {
          dispatch(selectedDegreeChanage(response.data.data?.degreePursue || 'master'))
          dispatch(updateUserField({ key: 'degreePursue', value: response.data.data?.degreePursue || "master" }));
          const res = response?.data?.data
          if (res?.discipline !== null && Array.isArray(res?.highPriorities) && res?.highPriorities?.length > 0) {
            dispatch(setIsAllReInfoFilled(true));
          }
          else {
            dispatch(setIsAllReInfoFilled(false));
          }
        }
      } catch (e: any) {
        console.error("User preference fetch failed:", e.response?.data?.message ?? e.message);
      }
    };

    try {
      const status = sessionStorage.getItem('register_status');
      if (status === '201') {
        doFetch();
      } else {
        const onRegister = () => {
          // run once then remove
          doFetch();
          try { window.removeEventListener('registerSuccess', onRegister); } catch (e) { }
        };
        window.addEventListener('registerSuccess', onRegister);
      }
    } catch (e) {
      console.error('Error checking register_status, calling preference fetch immediately', e);
      doFetch();
    }
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated) {
      getUserPreference();
      dispatch(setSearchType("College"));
    }
  }, [getUserPreference, isAuthenticated, dispatch]);

  const handleInputFocus = () => {
    if (searchText) {
      setIsDropdownVisible(true);
    }
  };

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchedList]);

  const [searchLoading, setSearchLoading] = useState(false);
  const [inputLocked, setInputLocked] = useState(false);

  const postSearchCollege = async (value: string) => {
    try {
      let data = {
        searchTerm: value
      }
      setSearchLoading(true);
      dispatch(setSearchedList([]));
      const response = await API.post(`search`, data);
      if (response.data.data) {
        const results = response.data.data;
        dispatch(setSearchedList(results));
      }
    } catch (e: any) {
      console.error("Search failed:", e.response?.data?.message ?? e.message);
      if (e.response?.status !== 429 && e.response?.status !== 404 && e.response?.status !== 500) {
        ToastMsg("error", e.response?.data?.message ?? e.message);
      }
    }
    finally {
      setSearchLoading(false);
    }
  }

  const postDregreePurse = async (value: string) => {
    try {
      let data = {
        degreePursue: value
      }
      const response = await API.put('user-preference', data);
      if (response.data.message) {
        console.log(`Degree preference updated to: ${value}`);
      }
    } catch (e: any) {
      console.error("Degree preference update failed:", e.response?.data?.message ?? e.message);
    }
  }

  const handleDegreeChange = (value: string) => {
    postDregreePurse(value)
    dispatch(selectedDegreeChanage(value));
    dispatch(updateUserField({ key: 'degreePursue', value }));
    dispatch(clearAllFilters());
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(degreeSearchChange(value));
    setIsDropdownVisible(!!value);
    // If user is typing, clear the localStorage (not a selection)
    localStorage.removeItem('selectedSearchText');
    if (value) {
      postSearchCollege(value);
    }
  };

  const navigateToHome = () => {
    window.location.href = "/";
  };
  // Enhanced: handle /search tab click with segment logic
  const handleChangeTabs = (key: string) => {
    const headerRestrictedPaths = ['/', '/recommend-college-dream', '/recommend-college-safe', '/recommend-college-target', '/recommend-course', '/mylist-college', '/mylist-course', '/talk-to-expert', '/referral', '/compare-course', '/fasttrack', '/role'];
    if ((mobileUnverifiedUser) && headerRestrictedPaths.includes(key)) {
      onOpenMobileVerificationModal?.();
      return;
    }

    if (isFreeUser && headerRestrictedPaths.includes(key)) {
      navigate("/upgrade");
      ToastMsg('warning', 'Please upgrade to access this feature.');
      return;
    }

    if (key === '/college') {
      dispatch(setIsCourse(false));
      dispatch(setIsRole(false));
      dispatch(setSearchType('College'));
      dispatch(clearAllFilters());
      localStorage.removeItem("filters");
    } else if (key === '/course') {
      dispatch(setIsCourse(true));
      dispatch(setIsRole(false));
      dispatch(setSearchType('Course'));
      dispatch(clearAllFilters());
      localStorage.removeItem("filters");
    } else if (key === '/role') {
      dispatch(setIsRole(true));
      dispatch(setIsCourse(false));
      dispatch(setSearchType('Role'));
      dispatch(clearAllFilters());
      localStorage.removeItem("filters");
    }

    if (key === '/search') {
      if (searchText && searchText.trim() !== "") {
        navigate("/search");
        return;
      }
      if (location.pathname.startsWith("/college")) {
        dispatch(setSearchType("College"));
        localStorage.setItem("searchType", "College");
      } else if (location.pathname.startsWith("/course")) {
        dispatch(setSearchType("Course"));
        localStorage.setItem("searchType", "Course");
      }
      navigate("/search");
      return;
    }
    if (location.pathname === key) {
      window.location.href = key;
    } else {
      navigate(key);
    }
  };
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const upgradeBtn = useMemo(() => blackFilledBtn('Upgrade', <img src={crownIcon} alt="Upgrade" />), []);


  const handleClearSearch = () => {
    dispatch(degreeSearchChange(''));
    setIsDropdownVisible(false);
    dispatch(setIsCourse(false));
    dispatch(setIsRole(false));
    dispatch(setSearchType(null));
    dispatch(clearAllFilters());
    localStorage.removeItem('selectedSearchText');
    localStorage.removeItem('searchContext');
    setInputLocked(true);
    // Optionally, you can trigger a reload or navigation to /course to ensure CoursePage picks up the change
    if (location.pathname === '/course') {
      // This will cause CoursePage to refetch with searchType=null
      navigate('/course', { replace: true });
    }
  };
  const containerRef = useRef<HTMLDivElement>(null);


  const handleOpen = () => setIsModalVisible(true);
  const handleClose = () => setIsModalVisible(false);
  // Enhanced: Custom navigation to /search with segment logic
  const handleNavigateToSearch = useCallback(() => {
    setInputLocked(false);
    // If there is search text, do not change segment
    if (searchText && searchText.trim() !== "") {
      navigate("/search");
      return;
    }
    // No search text: set segment based on current route
    if (location.pathname.startsWith("/college")) {
      dispatch(setSearchType("College"));
      localStorage.setItem("searchType", "College");
    } else if (location.pathname.startsWith("/course")) {
      dispatch(setSearchType("Course"));
      localStorage.setItem("searchType", "Course");
    }
    navigate("/search");
  }, [searchText, location.pathname, dispatch, navigate]);

  const renderCollegeSearch = (fromMobile = false, onClearSearch?: () => void) => {
    let dropdownContent = null;

    if (searchText && isDropdownVisible) {
      const containerEl = fromMobile
        ? document.querySelector('.mobileDegreeSearch')
        : containerRef.current;

      if (!containerEl) return null;

      const rect = containerEl.getBoundingClientRect();

      const containerWidth = rect.width;
      const containerLeft = rect.left;

      const clampedWidth = Math.min(containerWidth, window.innerWidth - 32);
      const clampedLeft = Math.max(containerLeft, 16);

      let dropdownInnerContent: React.ReactNode;
      if (searchLoading) {
        dropdownInnerContent = (
          <div style={{ padding: "12px" }}>
            <Skeleton active paragraph={{ rows: 3 }} style={{ height: "20px" }} title={false} />
            <Skeleton active paragraph={{ rows: 3 }} style={{ height: "20px" }} title={false} />
            <Skeleton active paragraph={{ rows: 3 }} style={{ height: "20px" }} title={false} />
          </div>
        );
      } else if (searchedList.length > 0) {
        dropdownInnerContent = searchedList.map((item: {
          id: string;
          displayName: string;
          type: string;
          unitId?: string;
          cipCode?: number;
          location?: { city?: string; state?: string };
        }, idx: number
        ) => {
          return (
            <button
              key={item.id}
              className={`dropdown-item ${activeIndex === idx ? "active-item" : ""}`}
              style={{
                background: activeIndex === idx ? "#0056D2" : "transparent"
              }}
              onMouseDown={e => e.stopPropagation()}
              onClick={() => handleCollegeNav(item)}
              ref={el => {
                if (activeIndex === idx && el) {
                  el.scrollIntoView({
                    block: "nearest",
                    behavior: "smooth"
                  });
                }
              }}
            >

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text
                  className={`itemTitle ${(item.type === "course" || item.type === "role") ? "course" : ""}`}
                  style={{
                    color:
                      ((item.type === "college" && (item as any).fasttrack !== 1) || item.type === "role") && (mobileUnverifiedUser || isFreeUser)
                        ? "#aeaeae"
                        : (item.type === "course" || item.type === "role")
                          ? 'black'
                          : ''
                  }}
                >
                  {item.displayName}
                </Text>

                {item.location && (
                  <Text className="item-sub" style={{
                    color:
                      ((item.type === "college" && (item as any).fasttrack !== 1) || item.type === "role") && (mobileUnverifiedUser || isFreeUser)
                        ? "#aeaeae"
                        : ''
                  }}>
                    {`${item.location?.city}, ${item.location?.state}`}
                  </Text>
                )}
              </div>
              {((item.type === "college" && (item as any).fasttrack !== 1) || item.type === "role") && (mobileUnverifiedUser || isFreeUser) ? (
                <span className="mobile-verify-indicator" title={isFreeUser ? "Upgrade to view" : "Verify mobile to view"}>
                  <img src={lockIcon} alt="Lock Icon" style={{ width: "12px", height: "12px", marginRight: '8px' }} />
                </span>
              ) : (
                <Text
                  className={`itemType ${(item.type === "course" || item.type === "role") ? "course" : ""}`}
                  style={{ color: (item.type === "course" || item.type === "role") ? 'black' : '' }}
                >
                  {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                </Text>
              )}
            </button>
          );
        });
      } else {
        dropdownInnerContent = (
          <div className="no-result-dropdown" style={{
            color: 'black',
            padding: '12px',
            width: "100%",
            height: "50px",
            marginTop: "-20px",
            marginBottom: "20px",
            overflowY: "hidden"
          }}>
            No results found!
          </div>
        );
      }

      dropdownContent = (
        (location.pathname === "/search" && (<div
          className="search-dropdown"
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: fromMobile ? '120px' : '50px',
            left: `${clampedLeft}px`,
            width: `${clampedWidth}px`,
            maxWidth: 'calc(100vw - 32px)',
            zIndex: 9999,
            background: '#fff',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {dropdownInnerContent}
        </div>))
      );
    }



    return (
      <div className="animated-placeholder-wrapper" ref={wrapperRef}>
        <Input
          className="search-input"
          prefix={<SearchOutlined style={{ marginRight: '10px', color: '#A3A3A3' }} />}
          value={searchText}
          onChange={handleSearchChange}
          onFocus={() => { if (!inputLocked) handleInputFocus(); }}
          readOnly={inputLocked}
          bordered={false}
          placeholder={!searchText ? '' : undefined}
          onClick={e => {
            // Only navigate if not clicking the clear button
            if (!(e.target as HTMLElement).closest('.ant-btn')) {
              handleNavigateToSearch();
            }
          }}
          suffix={
            searchText && (
              <Button
                icon={<img src={searchCloseIcon} alt="close" style={{ cursor: 'pointer', marginTop: "6px" }} />}
                onClick={onClearSearch}
                style={{ cursor: 'pointer', all: "unset", color: '#E0E0E0' }}
              />
            )
          }
        />
        {!searchText && <RotatingWords words={[searchType === 'Course' ? 'Search Major' : searchType === 'Role' ? 'Search Role' : 'Search College']} />}
        {dropdownContent}
      </div>
    );
  };

  const getCollegeSlug = async (id: string) => {
    try {
      const response = await API.get(`college/?unitId=${id}`);
      if (response.status === 200) {
        navigate(`/college/${response.data.data?.collegeSlug}`)
      }
    } catch (e: any) {
      console.error("Failed to fetch college slug:", e.response?.data?.message ?? e.message);
      if (e.response?.status !== 429 && e.response?.status !== 404 && e.response?.status !== 500) {
        ToastMsg("error", e.response?.data?.message ?? e.message);
      }
    }
  }

  const getCourseSlug = (cipCode: number) => {
    try {
      dispatch(setIsCourse(true));
      dispatch(setCourseCipCode(cipCode));
      dispatch(clearAllFilters());

      if (location.pathname === '/search') {
        console.info("Already in search page");
      } else {
        navigate(`/search`);
      }
    }
    catch (e: any) {
      console.error("Failed to navigate to course:", e.response?.data?.message ?? e.message);
      dispatch(degreeSearchChange(''));
      ToastMsg("error", e.response?.data?.message ?? e.message);
    }
  }

  const getRoleSlug = (socCode: number) => {
    try {
      dispatch(setIsRole(true));
      dispatch(setIsCourse(false));
      dispatch(setCourseSocCode(socCode));
      dispatch(clearAllFilters());

      if (location.pathname === '/search') {
        console.info("Already in search page");
      } else {
        navigate(`/search`);
      }
    }
    catch (err) {
      console.error("Failed to navigate to role:", err);
      dispatch(degreeSearchChange(''));
      ToastMsg("error", "Failed to navigate to role");
    }
  }

  const handleCollegeNav = (item: any) => {
    dispatch(degreeSearchChange(item.displayName));
    localStorage.setItem('selectedSearchText', item.displayName);
    if (item.type === "college") {
      getCollegeSlug(item.unitId)
    }
    else if (item.type === "course") {
      getCourseSlug(item.cipCode)
    }
    else if (item.type === "role") {
      getRoleSlug(item.socCode)
    }
    setIsDropdownVisible(false);
  }

  const getActiveTabKey = (pathname: string) => {
    if (pathname.startsWith('/recommend')) {
      return '/recommend-college-dream';
    }
    if (pathname.startsWith('/mylist')) {
      return '/mylist-college';
    }
    return pathname;
  };

  const getUserPersonal = useCallback(async () => {
    if (sessionStorage.getItem('is_logging_out') === 'true') return;
    const doFetch = async () => {
      try {
        const response = await API.get("user-personal");
        if (response.status === 200 && response.data?.data) {
          const data = response.data.data;
          if (data?.phoneVerified === true) {
            dispatch(setMobileUnverifiedUser(false));
          } else {
            dispatch(setMobileUnverifiedUser(true));
          }

          if (data?.currentPlan === "guest-user") {
            dispatch(setCurrentPlan("guest-user"));
          }
          if (data?.currentPlan === "free-trial") {
            dispatch(setCurrentPlan("free-trial"))
          }
          if (data?.currentPlan === "yearly-standard") {
            dispatch(setCurrentPlan("yearly-standard"))
          }
          if (data?.currentPlan === "yearly-premium") {
            dispatch(setCurrentPlan("yearly-premium"))
          }

          if (currentPlan === "guest-user" && mobileUnverifiedUser === false) {
            dispatch(setIsFreeUser(true));
            console.log("free user? ", freeUser)
          }
          else {
            dispatch(setIsFreeUser(false))
          }

          if (data.upgradeRequest === true) {
            dispatch(setIsUpgraded(true));
            localStorage.setItem("isUpgraded", "true");
          } else {
            dispatch(setIsUpgraded(false));
            localStorage.setItem("isUpgraded", "false");
          }
        } else {
          dispatch(setIsUpgraded(false));
          localStorage.setItem("isUpgraded", "false");
        }
      } catch (e: any) {
        console.error("Error fetching user plan:", e);
        if (e.response?.status !== 429 && e.response?.status !== 404 && e.response?.status !== 500 && !e._skipToast) {
          ToastMsg("error", e.response?.data?.message ?? e.message);
        }
        dispatch(setIsUpgraded(false));
        localStorage.setItem("isUpgraded", "false");
      }
    };

    try {
      const status = sessionStorage.getItem('register_status');
      if (status === '201') {
        doFetch();
      } else {
        const onRegister = () => {
          doFetch();
          try { window.removeEventListener('registerSuccess', onRegister); } catch (e) { }
        };
        window.addEventListener('registerSuccess', onRegister);
      }
    } catch (e) {
      console.error('Error checking register_status, calling user-personal fetch immediately', e);
      doFetch();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    getUserPersonal();
  }, [getUserPersonal]);

  return (
    <div className="appHeaderContainer">
      <Header className="nav-header" style={{ backgroundColor: 'white' }}>
        <div className="logo-container">
          <button
            className="logo-button"
            onClick={navigateToHome}
            onKeyDown={(e) => e.key === 'Enter' && navigateToHome()}
            aria-label="Go to Home"
            style={{ background: 'none', border: 'none', padding: 0 }}
          >
            <img
              src={LogoPrimary}
              className="logo-primary"
              alt="Primary Logo"
              width={135}
              height={32}
              // @ts-expect-error React 18.3 types declare fetchPriority but the runtime doesn't handle it; lowercase is the correct HTML attribute
              fetchpriority="high"
            />
          </button>
        </div>
        {(location.pathname === "/mobile-verification") &&
          <div className="logoutButton">
            <Button onClick={handleLogout}>Log out</Button>
          </div>
        }
        {(location.pathname !== "/mobile-verification" && location.pathname !== "/onboard" && location.pathname !== "/referral") &&
          <>
            <div className="navigation-menu">
              {location.pathname !== "/search" && <div className="degree-search-container" ref={containerRef}>
                <Select
                  value={selectedDegree}
                  onChange={handleDegreeChange}
                  bordered={false}
                  className="degree-select"
                  options={DEGREE_OPTIONS}
                  style={{ borderRight: '1px solid #E0E0E0' }}
                  dropdownStyle={{ width: 'auto', minWidth: '30px', top: "60px", position: "fixed" }}
                />
                {renderCollegeSearch(false, handleClearSearch)}
              </div>}


              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }} ref={navTabsRef}>
                <Tabs
                  key={tabsKey}
                  activeKey={getActiveTabKey(location.pathname)}
                  onTabClick={(key) => handleChangeTabs(key)}
                  items={visibleTabs.map((item) => ({
                    key: item.key,
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {item.icon}
                        {item.label}
                      </span>
                    ),
                  }))}
                  className="custom-tabs"
                  centered
                  tabBarStyle={{ borderBottom: 'none', margin: 0 }}
                />
                {overflowTabs.length > 0 && (
                  <Dropdown
                    menu={{
                      items: overflowTabs.map((item) => ({
                        key: item.key,
                        label: item.label,
                        onClick: () => handleChangeTabs(item.key),
                      })),
                    }}
                    trigger={['click']}
                    placement="bottomCenter"
                  >
                    <div
                      className="more-options-container"
                      tabIndex={0}
                      aria-label="More Options"
                    >
                      <span className="more-options-dots">&#8226;&#8202;&#8226;&#8202;&#8226;</span>
                    </div>
                  </Dropdown>
                )}
              </div>

            </div>
            <div className="right-menus">
              {!isUpgraded &&
                (windowWidth > 1024 ? (
                  <div
                    className="talkToExpBtn"
                    onClick={() => navigate('/upgrade')}
                    style={{ marginTop: "12px" }}
                  >
                    {upgradeBtn}
                  </div>
                ) : (
                  <div
                    className="mobileTalkToExpBtn"
                    onClick={() => navigate('/upgrade')}
                  >
                    {blackFilledBtn('', <img src={crownIcon} alt="Upgrade" />)}
                  </div>
                ))
              }
              <Dropdown
                overlay={<UserMenu avatar={avatar} name={user?.name ?? ''} onLogout={handleLogout} />}
                trigger={['click']}
                placement="topRight"
                getPopupContainer={(triggerNode) => triggerNode.parentElement!}
                destroyPopupOnHide={true}
              >


                {avatar ? (
                  <img
                    src={avatar}
                    alt="User Avatar"
                    style={{ width: 40, height: 40, borderRadius: '50%', cursor: "pointer" }}
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#0056D2',
                      color: "white",
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      textTransform: 'uppercase'
                    }}
                  >
                    {user?.name?.[0] || 'U'}
                  </div>
                )}

              </Dropdown>
            </div>
          </>
        }
      </Header>
      {
        (location.pathname !== "/mobile-verification" && location.pathname !== "/onboard") &&
        (
          location.pathname !== "/search" && (
            <div className="mobileDegreeSearch">
              <button
                className="degreeTrigger"
                onClick={handleOpen}
                onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
                aria-label="Select Degree"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              >
                {DEGREE_OPTIONS.find(d => d.value === selectedDegree)?.label}
                <DownOutlined color="#6B7280" />
              </button>
              <Modal
                open={isModalVisible}
                closable={false}
                footer={null}
                centered
                className="degreeModal"
                onCancel={handleClose}
                width={"80%"}
              >
                <div className="degreeModalHeader">
                  <Text strong style={{ fontSize: '18px', color: '#6B7280' }}>Select Degree</Text>
                  <CloseOutlined onClick={handleClose} style={{ cursor: 'pointer', backgroundColor: 'black', color: 'white', padding: "8px", borderRadius: '50%' }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {DEGREE_OPTIONS.map(degree => (
                    <Button
                      key={degree.value}
                      type={selectedDegree === degree.value ? 'primary' : 'default'}
                      onClick={() => handleDegreeChange(degree.value)}
                      block
                      className="degreeOptionButton"
                    >
                      <Checkbox
                        checked={selectedDegree === degree.value}
                        style={{ marginRight: '12px', transform: 'scale(1.75)' }}
                      />
                      {degree.label}
                    </Button>
                  ))}
                </div>
              </Modal>
              {renderCollegeSearch(true, handleClearSearch)}
            </div>
          )
        )
      }

    </div >
  );
}

export default React.memo(NewAppHeader);